import { TabsWithContent, useQueryState } from "@homebound/beam";
import { useMemo } from "react";
import { Loading } from "src/components";
import {
  BidContractPlanPricing_RevisionFragment,
  useBidContractPlanPricing_ReadyPlanOptionsQuery,
  useBidContractPlanPricing_RevisionQuery,
  useBidContractPlanPricing_ScopeLinesQuery,
} from "src/components/bid-contracts/BidContractPlanPricingPage.generated";
import { useRouteParams } from "src/hooks/useRouteParams";
import { Error } from "src/routes/Error";
import { ProrateBidView } from "src/routes/bids/components/ProrateBidModal";
import { fail, isDefined, queryResult } from "src/utils";

export function BidContractPlanPricingPage() {
  const { bidContractRevisionId } = useRouteParams<{ bidContractRevisionId: string }>();
  const query = useBidContractPlanPricing_RevisionQuery({ variables: { bidContractRevisionId } });
  return queryResult(query, ({ bidContractRevision }) => (
    <BidContractPlanPricingView bidContractRevision={bidContractRevision} />
  ));
}

type BidContractPlanPricingViewProps = { bidContractRevision: BidContractPlanPricing_RevisionFragment };

function BidContractPlanPricingView(props: BidContractPlanPricingViewProps) {
  const { bidContractRevision } = props;
  // Render tabs based on the product offerings on the revision
  const [sortedPofs, tlisByPof] = useMemo(
    () => [
      bidContractRevision.tradeLineItems
        .map((tli) => tli.productOffering)
        .uniqueByKey("id")
        .sortBy((pof) => pof.id),
      bidContractRevision.tradeLineItems.groupBy((tli) => tli.productOffering.id),
    ],
    [bidContractRevision.tradeLineItems],
  );
  const [selectedTab, setSelectedTab] = useQueryState<string>("productOfferingId", sortedPofs[0].id);
  const selectedReadyPlan = sortedPofs.find((pof) => pof.id === selectedTab) ?? fail("ProductOffering not found");
  const bclis = tlisByPof[selectedTab]?.flatMap((tli) => tli.lineItems) ?? [];
  // Pull the scope lines from the bclis instead of the bid package version as we are deleting PBBV1
  const query = useBidContractPlanPricing_ScopeLinesQuery({
    variables: { bcliFilter: { id: bclis.map((bcli) => bcli.id) } },
  });
  const takeoffLineItemVersions = useMemo(() => {
    return query.data?.bidContractLineItemsPage.entities.map((bcli) => bcli.takeoffLineItemVersion).compact() ?? [];
  }, [query.data?.bidContractLineItemsPage.entities]);
  // Only the options our scope lines actually reference — see PlanBasedBidsTable.graphql
  const optionIds = useMemo(
    () => takeoffLineItemVersions.flatMap((tliv) => tliv.options.map((o) => o.id)).unique(),
    [takeoffLineItemVersions],
  );
  // See PlanBasedBidsTable.graphql — options are looked up by id so that options removed from the plan
  // since the bid package was drafted still resolve.
  const readyPlanOptionsQuery = useBidContractPlanPricing_ReadyPlanOptionsQuery({
    variables: { optionIds },
    skip: optionIds.isEmpty,
  });
  const readyPlanOptionsById = useMemo(
    () => readyPlanOptionsQuery.data?.readyPlanOptions.keyBy((option) => option.id) ?? {},
    [readyPlanOptionsQuery.data?.readyPlanOptions],
  );
  // The options query is keyed off ids read out of the scope lines, so it lands a beat later; without
  // this the view would render against an empty map and `getReadyPlanOptions` would fail.
  const optionsReady = optionIds.isEmpty || isDefined(readyPlanOptionsQuery.data);

  return (
    <TabsWithContent
      alwaysShowAllTabs
      tabs={sortedPofs.map((pof) => ({
        name: pof.name,
        value: pof.id,
        render: () => {
          // Surface option query failures directly; otherwise `readyPlanOptionsById` falls back to an
          // empty map and every option lookup blows up with a misleading "ReadyPlanOption not found".
          if (readyPlanOptionsQuery.error) return <Error error={readyPlanOptionsQuery.error} />;
          if (query.loading || readyPlanOptionsQuery.loading || !optionsReady) return <Loading />;
          return queryResult(query, () => (
            <ProrateBidView
              readyPlan={selectedReadyPlan}
              readyPlanOptionsById={readyPlanOptionsById}
              scopeLineItems={takeoffLineItemVersions}
              type={bidContractRevision.bidContract.type!.code}
              existingTradeLineItems={bidContractRevision.tradeLineItems}
              disabled
            />
          ));
        },
      }))}
      selected={selectedTab}
      onChange={setSelectedTab}
    />
  );
}
